import type { ReactNode } from "react";

import { localizedHref } from "../i18n/lang";

const TONE = {
  teal: "bg-tile-teal text-ink",
  mint: "bg-tile-mint text-ink",
  sun: "bg-tile-sun text-ink",
  peach: "bg-tile-peach text-ink",
  sage: "bg-tile-sage text-ink",
  stone: "bg-tile-stone text-ink",
} as const;

interface TileProps {
  tone: keyof typeof TONE;
  icon?: ReactNode;
  title: string;
  sub: string;
  href: string;
  label?: string;
  external?: boolean;
}

/** The pastel tile grid used by Activities and Resources.
 * Not consumed by this task's Home page — its first caller lands in B2
 * (see the "entry" list in .fallowrc.jsonc for why the file itself isn't
 * flagged as dead code in the meantime). */
export function Tile({
  tone,
  icon,
  title,
  sub,
  href,
  label = "Open",
  external,
}: TileProps) {
  return (
    <a
      href={localizedHref(href, external)}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      className={`group flex min-h-56 flex-col justify-between rounded-2xl p-7 shadow-soft transition duration-300 hover:-translate-y-1 hover:shadow-lift focus-visible:-translate-y-1 focus-visible:shadow-lift focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-deep ${TONE[tone]}`}
    >
      <div>
        {icon ? <div className="mb-4 text-ink/80">{icon}</div> : null}
        <h3 className="font-display text-[1.35rem] leading-snug text-ink">
          {title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-ink-soft">{sub}</p>
      </div>
      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink">
        {label}
        <span
          aria-hidden="true"
          className="transition-transform duration-300 group-hover:translate-x-1 group-focus-visible:translate-x-1"
        >
          →
        </span>
      </span>
    </a>
  );
}
